import { useState } from 'react';
import { X, CheckCircle, HelpCircle, XCircle, Paperclip } from 'lucide-react';
import { Button } from '../../ui/button';
import { purchasingAPI, DeliveryAppeal } from '../../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { useFormatters } from '../../../contexts/PreferencesContext';

interface AppealResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  appeal: DeliveryAppeal;
  // The action is forwarded so the parent can perform an accurate optimistic
  // update on the linked row (e.g. flip the appeal-status badge + drop the
  // Respond / Accept / Query / Reject buttons) before the refetch returns.
  onSuccess: (action: 'accept' | 'query' | 'reject') => void;
}

export default function AppealResponseModal({ isOpen, onClose, appeal, onSuccess }: AppealResponseModalProps) {
  const [responseNote, setResponseNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { formatDate } = useFormatters();

  if (!isOpen) return null;

  const handleRespond = async (action: 'accept' | 'query' | 'reject') => {
    // CR-4652: Notes required for query and reject
    if ((action === 'query' || action === 'reject') && !responseNote.trim()) {
      toast.error(`Please provide a note when ${action === 'query' ? 'querying' : 'rejecting'} an appeal`);
      return;
    }

    try {
      setSubmitting(true);
      await purchasingAPI.respondToDeliveryAppeal(appeal.id, {
        action,
        response_note: responseNote,
      });

      // CR-4652: Upload attachments if any
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await purchasingAPI.uploadAppealAttachment(appeal.id, formData);
      }

      const labels: Record<string, string> = { accept: 'accepted', query: 'queried', reject: 'rejected' };
      toast.success(`Appeal ${labels[action]} successfully`);
      onSuccess(action);
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to respond to appeal'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
            <h3 className="text-lg font-bold">Respond to Appeal</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Appeal Details */}
          <div className="p-5 space-y-4">
            <div className="bg-gray-50 rounded-lg p-3 text-sm grid grid-cols-2 gap-2">
              <div><span className="text-gray-500">GRN:</span> <span className="font-medium">{appeal.grn_number}</span></div>
              <div><span className="text-gray-500">PO:</span> <span className="font-medium">{appeal.po_number}</span></div>
              <div><span className="text-gray-500">Part:</span> <span className="font-medium">{appeal.part_number}</span></div>
              <div><span className="text-gray-500">Appeal #:</span> <span className="font-medium">{appeal.appeal_sequence}</span></div>
            </div>

            {/* Claimed vs Current Values */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Field</th>
                    <th className="px-3 py-2 text-left font-medium">Current</th>
                    <th className="px-3 py-2 text-left font-medium text-orange-600">Claimed</th>
                  </tr>
                </thead>
                <tbody>
                  {appeal.qty_claimed && (
                    <tr className="border-t">
                      <td className="px-3 py-2">Quantity</td>
                      <td className="px-3 py-2">{appeal.grn_qty_received}</td>
                      <td className="px-3 py-2 font-medium text-orange-600">{appeal.qty_claimed}</td>
                    </tr>
                  )}
                  {appeal.date_claimed && (
                    <tr className="border-t">
                      <td className="px-3 py-2">Delivery Date</td>
                      <td className="px-3 py-2">{appeal.original_receipt_date ? formatDate(appeal.original_receipt_date) : appeal.grn_receipt_date ? formatDate(appeal.grn_receipt_date) : '-'}</td>
                      <td className="px-3 py-2 font-medium text-orange-600">{formatDate(appeal.date_claimed)}</td>
                    </tr>
                  )}
                  {appeal.odd_claimed && (
                    <tr className="border-t">
                      <td className="px-3 py-2">ODD</td>
                      <td className="px-3 py-2">{appeal.grn_delivery_date ? formatDate(appeal.grn_delivery_date) : '-'}</td>
                      <td className="px-3 py-2 font-medium text-orange-600">{formatDate(appeal.odd_claimed)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {appeal.supplier_note && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  {appeal.is_customer_appeal ? 'Customer Note' : 'Supplier Note'}
                </label>
                <p className="mt-1 text-sm bg-orange-50 p-3 rounded-lg">{appeal.supplier_note}</p>
              </div>
            )}

            {/* CR-4652: Responder note — required for query/reject. Label
                flips with the appeal direction so the responder sees their
                own role (the model field is named customer_note for legacy
                reasons but semantically holds whichever side responded). */}
            <div>
              <label className="text-sm font-medium block mb-1">
                {appeal.is_customer_appeal ? 'Supplier Note' : 'Customer Note'}
                {' '}<span className="text-xs text-gray-400">(required for Query / Reject)</span>
              </label>
              <textarea
                value={responseNote}
                onChange={(e) => setResponseNote(e.target.value)}
                placeholder="Add a note explaining your decision..."
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* CR-4652: Attachments */}
            <div>
              <label className="text-sm font-medium block mb-1">
                <Paperclip className="h-3.5 w-3.5 inline mr-1" />
                Attachments
              </label>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif"
                onChange={(e) => {
                  if (e.target.files) setFiles(Array.from(e.target.files));
                }}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
              {files.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{files.length} file(s) selected</p>
              )}
            </div>
          </div>

          {/* CR-4739: Footer with action buttons — hidden when appeal is already resolved */}
          <div className="flex justify-between items-center p-5 border-t sticky bottom-0 bg-white">
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            {['accepted', 'rejected', 'cancelled'].includes(appeal.status) ? (
              <span className="text-sm text-gray-500 italic">
                This appeal has been {appeal.status}.
              </span>
            ) : (
            <div className="flex gap-2">
              <Button
                onClick={() => handleRespond('reject')}
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
              <Button
                onClick={() => handleRespond('query')}
                disabled={submitting}
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                <HelpCircle className="h-4 w-4 mr-1" />
                {appeal.is_customer_appeal ? 'Query Customer' : 'Query Supplier'}
              </Button>
              <Button
                onClick={() => handleRespond('accept')}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Accept
              </Button>
            </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
